import React from 'react';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { Box, Typography, useTheme } from '@mui/material';
import { TrackerDataType } from '../../shared/constants/constants';
import { fontWeights } from '../../theme/typography';
import dayjs from '../../utils/dayjs';
import { calendarSnapshotChipTypography } from './CalendarSnapshotChip';
import { calendarCategoryColors, getCalendarUiPalette } from './calendarPalette';
import { CalendarEvent } from './types';

function getLogIcon(logtype: TrackerDataType) {
  switch (logtype) {
    case TrackerDataType.SERVICE:
      return AccountTreeOutlinedIcon;
    case TrackerDataType.MEDIA_INTERACTION:
      return NewspaperOutlinedIcon;
    case TrackerDataType.ENGAGEMENT:
      return HandshakeOutlinedIcon;
    case TrackerDataType.CONTENT_LOG:
      return DescriptionOutlinedIcon;
    default:
      return AccountTreeOutlinedIcon;
  }
}

interface CalendarSearchResultRowProps {
  event: CalendarEvent;
}

export default function CalendarSearchResultRow({ event }: Readonly<CalendarSearchResultRowProps>) {
  const theme = useTheme();
  const calendarUiPalette = getCalendarUiPalette(theme);
  const color = calendarCategoryColors[event.logtype] ?? calendarUiPalette.searchResults.fallbackCategory;
  const LogIcon = getLogIcon(event.logtype);

  return (
    <Box
      data-cy="calendar-search-result-row"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.25 }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '999px',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: calendarUiPalette.searchResults.iconForeground,
          boxShadow: calendarUiPalette.searchResults.iconShadow,
        }}
      >
        <LogIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: theme.typography.body2.fontFamily,
            fontWeight: fontWeights.semibold,
            fontSize: '0.75rem',
            lineHeight: calendarSnapshotChipTypography.lineHeight,
            letterSpacing: calendarSnapshotChipTypography.letterSpacing,
            color: calendarUiPalette.searchResults.titleText,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {event.primaryTitle}
        </Typography>
        {event.secondaryMeta?.value ? (
          <Typography
            sx={{
              color: calendarUiPalette.searchResults.metaText,
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              lineHeight: 1.3,
            }}
          >
            {event.secondaryMeta.value}
          </Typography>
        ) : null}
      </Box>
      <Typography
        sx={{
          color: calendarUiPalette.searchResults.metaText,
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          ml: 1,
        }}
      >
        {dayjs(event.calendarDate).format('DD MMM YYYY')}
      </Typography>
    </Box>
  );
}
